import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { format, parseISO } from 'date-fns';
import clsx from 'clsx';

import { api, HttpError } from '../api';
import type { DailyNote, PickerSession } from '../types';
import CenteredOverlay from './CenteredOverlay';
import LiveMarkdownEditor from './LiveMarkdownEditor';

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

interface Props {
  date: string; // YYYY-MM-DD
  onClose: () => void;
}

export default function DailyNoteEditor({ date, onClose }: Props) {
  const qc = useQueryClient();
  const createTriggered = useRef(false);
  // Local draft. Seeded from the server once; thereafter the editor owns it
  // until a save round-trips, so refetches don't clobber in-progress edits.
  const [draft, setDraft] = useState<string | null>(null);

  const noteQ = useQuery({
    queryKey: ['daily-note', date],
    queryFn: () => api.get<DailyNote>(`/api/daily-notes/${date}`)
  });
  const note = noteQ.data;

  useEffect(() => {
    if (note && draft === null) setDraft(note.content);
  }, [note, draft]);

  function invalidate() {
    qc.invalidateQueries({ queryKey: ['daily-note', date] });
    qc.invalidateQueries({ queryKey: ['daily-notes'] });
  }

  const create = useMutation({
    mutationFn: () => api.post<DailyNote>(`/api/daily-notes/${date}`),
    onSuccess: (n) => {
      setDraft(n.content);
      qc.setQueryData(['daily-note', date], n);
      invalidate();
    }
  });

  // There's always "today's note": if today's note doesn't exist yet, create
  // it from the template the moment the editor opens (once).
  useEffect(() => {
    if (note && !note.exists && note.can_create && !createTriggered.current) {
      createTriggered.current = true;
      create.mutate();
    }
  }, [note, create]);

  const save = useMutation({
    mutationFn: (content: string) => api.put<DailyNote>(`/api/daily-notes/${date}`, { content }),
    onSuccess: (n) => {
      qc.setQueryData(['daily-note', date], n);
      invalidate();
    }
  });

  const dirty = note != null && draft != null && draft !== note.content;

  const [photoBusy, setPhotoBusy] = useState(false);
  const [photoMsg, setPhotoMsg] = useState<string | null>(null);
  const [photoNeedsConnect, setPhotoNeedsConnect] = useState(false);

  // Stop the picker poll loop when the editor unmounts — otherwise it keeps
  // polling (and could still attach a photo) after the popup is long gone.
  const unmounted = useRef(false);
  useEffect(() => {
    unmounted.current = false;
    return () => {
      unmounted.current = true;
    };
  }, []);

  // Google Photos picker: open a session, pop the Google-hosted picker, poll
  // until the user has chosen, then let the backend download + attach it. Base
  // URLs expire fast, so the attach step (download → vault) happens server-side
  // immediately; here we just drive the session and refresh the note.
  async function addPhoto() {
    setPhotoBusy(true);
    setPhotoMsg(null);
    setPhotoNeedsConnect(false);
    let win: Window | null = null;
    try {
      const s = await api.post<PickerSession>('/api/photos/picker-session');
      win = window.open(s.picker_uri, 'rialto-google-photos', 'width=520,height=680');
      const deadline = Date.now() + s.timeout_ms;
      // Google flips `mediaItemsSet` a beat after telling the user to "go back
      // to your app", so a closed popup isn't proof they abandoned — keep
      // polling for a grace window before giving up.
      let closedAt: number | null = null;
      while (Date.now() < deadline) {
        await sleep(s.poll_interval_ms);
        if (unmounted.current) return;
        const st = await api.get<{ media_items_set: boolean }>(
          `/api/photos/picker-session/${s.session_id}`
        );
        if (st.media_items_set) {
          const n = await api.post<DailyNote>(`/api/daily-notes/${date}/photo`, {
            session_id: s.session_id
          });
          setDraft(n.content);
          qc.setQueryData(['daily-note', date], n);
          invalidate();
          return;
        }
        if (win && win.closed) {
          if (closedAt == null) closedAt = Date.now();
          else if (Date.now() - closedAt > 8000) {
            setPhotoMsg('Picker closed — no photo added.');
            return;
          }
        }
      }
      setPhotoMsg('Timed out waiting for a selection.');
    } catch (err) {
      if (err instanceof HttpError && err.status === 409) {
        setPhotoNeedsConnect(true);
      } else {
        setPhotoMsg(err instanceof Error ? err.message : 'Could not add photo.');
      }
    } finally {
      win?.close();
      setPhotoBusy(false);
    }
  }

  const heading = format(parseISO(date), 'EEEE, MMMM d');

  function onKeyDown(e: React.KeyboardEvent) {
    if ((e.metaKey || e.ctrlKey) && e.key === 's') {
      e.preventDefault();
      if (dirty && draft != null) save.mutate(draft);
    }
  }

  return (
    <CenteredOverlay ariaLabel={`Daily note — ${heading}`} onClose={onClose} maxWidth={640} maxHeight="85dvh">
      <div className="flex items-center justify-between border-b border-[var(--border)] px-4 py-2">
        <div className="flex items-baseline gap-2">
          <span aria-hidden>📓</span>
          <h2 className="m-0 text-sm font-semibold">{heading}</h2>
        </div>
        <button
          type="button"
          aria-label="close"
          onClick={onClose}
          className="grid h-6 w-6 place-items-center rounded text-[var(--text-muted)] hover:bg-[var(--bg-hover)] hover:text-[var(--text)]"
        >
          ✕
        </button>
      </div>

      <div className="flex min-h-0 flex-1 flex-col p-3" onKeyDown={onKeyDown}>
        {noteQ.isLoading && <div className="p-3 text-sm text-[var(--text-faint)]">Loading…</div>}

        {noteQ.isError && (
          <div className="p-3 text-sm text-[var(--today-fg)]">
            {(noteQ.error as Error)?.message ?? 'Failed to load note.'}
          </div>
        )}

        {note && !note.exists && note.can_create && (
          <div className={clsx('p-3 text-sm', create.isError ? 'text-[var(--today-fg)]' : 'text-[var(--text-faint)]')}>
            {create.isError
              ? ((create.error as Error)?.message ?? 'Could not create the note.')
              : 'Creating today’s note from your template…'}
          </div>
        )}

        {note && !note.exists && !note.can_create && (
          <div className="p-3 text-sm text-[var(--text-faint)]">
            No daily note for this day. New notes can only be created for today.
          </div>
        )}

        {note && note.exists && draft != null && (
          <>
            <LiveMarkdownEditor
              value={draft}
              onChange={setDraft}
              autoFocus
              className="min-h-[360px] flex-1"
              photo={{
                onPick: addPhoto,
                busy: photoBusy,
                needsConnect: photoNeedsConnect,
                message: photoMsg
              }}
            />
            <div className="mt-2 flex items-center justify-end gap-3">
              {save.isError && (
                <span className="mr-auto text-[0.75rem] text-[var(--today-fg)]">
                  {(save.error as Error)?.message ?? 'Save failed.'}
                </span>
              )}
              <span className="text-[0.72rem] text-[var(--text-faint)]">
                {save.isPending ? 'Saving…' : dirty ? 'Unsaved changes' : 'Saved'}
              </span>
              <button
                type="button"
                onClick={() => draft != null && save.mutate(draft)}
                disabled={!dirty || save.isPending}
                className="rounded-md bg-[var(--accent)] px-3 py-[6px] text-[0.8rem] font-medium text-[var(--accent-fg)] hover:opacity-90 disabled:opacity-50"
              >
                Save
              </button>
            </div>
          </>
        )}
      </div>
    </CenteredOverlay>
  );
}
